import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../../../../components/Button";
import Spinner from "../../../../components/Spinner";
import { AuthContext } from '../../../../contexts/AuthContext';
import { PermissionModulesEnum, PermissionActionsEnum, Modules } from "../../../../enums";
import { hasResourseAccess } from '../../../../utils/methods';

interface InvoiceTabProps {
  data: string;
  loading: boolean;
  onSendEmail: (e?: any) => void;
}

const InvoiceTab: React.FC<InvoiceTabProps> = ({ data, loading, onSendEmail }) => {
  const viewerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { isSystemAdmin, permissions } = useContext(AuthContext);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const hasEmailAccess = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Invoices]: [PermissionActionsEnum.Email] }] })

  const isMobileOrTablet = /Mobi|Android|Tablet|iPad/i.test(navigator.userAgent);

  useEffect(() => {
    if (loading) return;

    if (!data) {
      setPdfUrl(null);
      if (viewerRef.current) viewerRef.current.innerHTML = "";
      return;
    }

    const loadPdf = async () => {
      try {
        const blob = await fetch(`data:application/pdf;base64,${data}`).then(res => res.blob());
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);

        if (!isMobileOrTablet && viewerRef.current) {
          viewerRef.current.innerHTML = `
            <embed 
              src="${url}#navpanes=0" 
              type="application/pdf"
              width="100%" 
              height="600px" 
            />
          `;
        }
      } catch (error) {
        console.error("Error loading PDF:", error);
        setPdfUrl(null);
      }
    };

    loadPdf();

    return () => {
      if (viewerRef.current) viewerRef.current.innerHTML = "";
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [data, isMobileOrTablet, loading]);

  return (
    <div className="m-6">
      {
        hasEmailAccess && (
          <div className="d-flex justify-content-end gap-2 mb-4">
            <Button
              className="btn btn-primary"
              text={t("invoices.editPreviewPayment.pageTexts.form.invoiceTab.sendEmailBtn")}
              disabled={loading}
              onClick={onSendEmail}
            />
          </div>
        )
      }

      <div className="card bg-white">
        {
          loading ? (
            <div className='card-body text-center'>
              <Spinner />
            </div>
          ) : (
            <div className="card-body p-0">
              {isMobileOrTablet ? (
                pdfUrl && (
                  <Button
                    className="btn btn-primary m-3"
                    text={t("Open PDF")}
                    onClick={() => window.open(pdfUrl, "_blank")}
                  />
                )
              ) : (
                <div
                  ref={viewerRef}
                  style={{
                    width: "100%",
                    height: "600px"
                  }}
                />
              )}
            </div>
          )
        }
      </div>
    </div>
  );
};

export default InvoiceTab;
